// @ts-ignore
import type { Consumer, Subscription } from "@rails/actioncable";
import { Message, MessageThread, Task } from "../../core/types";
import { getCurrentSanitizedUrl } from "../../utils/pageUrlUtil";

enum ChannelAction {
  MESSAGE_CREATED = "message_created",
  MESSAGE_UPDATED = "message_updated",
  MESSAGE_THREAD_UPDATED = "message_thread_updated",
  TASK_UPDATED = "task_updated",
  TYPING_NOTIFICATION = "typing_notification"
}

export enum TypingState {
  TYPING = "typing",
  NOT_TYPING = "not_typing",
}

export interface TypingNotification {
  source: "widget" | "ai_inbox";
  typing_state: TypingState;
}

/**
 * Channel response from the Rails Chat::MessageThreadChannel. Discriminated union on
 * `action` so the `body` shape narrows automatically. Error responses (`ok: false`)
 * carry only `status`; action-less responses (server status pings) leave `body`
 * as `unknown`.
 */
type ChannelResponse = { ok: boolean; status: number } & (
  | { action: ChannelAction.MESSAGE_CREATED; body: Message }
  | { action: ChannelAction.MESSAGE_UPDATED; body: Message }
  | { action: ChannelAction.MESSAGE_THREAD_UPDATED; body: MessageThread }
  | { action: ChannelAction.TASK_UPDATED; body: Task }
  | { action: ChannelAction.TYPING_NOTIFICATION; body: TypingNotification }
  | { action?: undefined; body?: unknown }
);

export interface MessageThreadCallback {
  onConnected?: (data: { id: number }) => void;
  onDisconnected?: (data: { id: number }) => void;
  onRejected?: (data: { id: number }) => void;
  onMessageCreated?: (data: { id: number; message: Message }) => void;
  onMessageUpdated?: (data: { id: number; message: Message }) => void;
  onMessageThreadUpdated?: (data: { id: number; messageThread: MessageThread }) => void;
  onMessageError?: (data: { id: number; status: number }) => void;
  onTaskUpdated?: (data: { id: number; task: Task }) => void;
  onTypingNotification?: (data: { id: number; typingNotification: TypingNotification }) => void;
}

export class MessageThreadChannel {
  private readonly CHANNEL_NAME = "Chat::MessageThreadChannel";
  private subscription: Subscription | null = null;
  private consumer: Consumer;

  constructor(consumer: Consumer) {
    this.consumer = consumer;
  }

  onConnected(id: number, callback: MessageThreadCallback): void {
    console.log(`Connected to ${this.CHANNEL_NAME}#${id}`);
    callback.onConnected?.({ id });
  }

  onDisconnected(id: number, callback: MessageThreadCallback): void {
    console.log(`Disconnected from ${this.CHANNEL_NAME}#${id}`);
    callback.onDisconnected?.({ id });
  }

  onRejected(id: number, callback: MessageThreadCallback): void {
    console.log(`Subscription to ${this.CHANNEL_NAME}#${id} was rejected`);
    callback.onRejected?.({ id });
  }

  onReceived(id: number, response: ChannelResponse, callback: MessageThreadCallback): void {
    if (!response.ok) {
      console.error(`${this.CHANNEL_NAME} error:`, response);
      callback.onMessageError?.({ id, status: response.status });
      return;
    }

    switch (response.action) {
      case ChannelAction.MESSAGE_CREATED:
        callback.onMessageCreated?.({ id, message: response.body });
        return;
      case ChannelAction.MESSAGE_UPDATED:
        callback.onMessageUpdated?.({ id, message: response.body });
        return;
      case ChannelAction.MESSAGE_THREAD_UPDATED:
        callback.onMessageThreadUpdated?.({ id, messageThread: response.body });
        return;
      case ChannelAction.TASK_UPDATED:
        callback.onTaskUpdated?.({ id, task: response.body });
        return;
      case ChannelAction.TYPING_NOTIFICATION:
        callback.onTypingNotification?.({ id, typingNotification: response.body });
        return;
    }
  }

  subscribe(messageThread: MessageThread, callback: MessageThreadCallback = {}): Subscription {
    // Get the message thread ID and contact UUID
    const id = messageThread.id;
    const uuid = messageThread.contact.uuid;

    // Ensure that we unsubscribe from any existing subscription before creating a new one
    this.unsubscribe();

    // Create a new subscription
    this.subscription = this.consumer.subscriptions.create({ channel: this.CHANNEL_NAME, client_type: "widget", uuid, id }, {
      connected: () => this.onConnected(id, callback),
      disconnected: () => this.onDisconnected(id, callback),
      rejected: () => this.onRejected(id, callback),
      received: (response: ChannelResponse) => this.onReceived(id, response, callback)
    });
    return this.subscription as Subscription;
  }

  unsubscribe(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  /**
   * Sends a create_message frame on the channel.
   * Returns `true` when the frame was queued, `false` when the underlying
   * ActionCable consumer wasn't connected (so the message wasn't actually sent).
   * Callers should branch on the return value to surface a send failure.
   */
  createMessage(message: string, attachmentId: string | null): boolean {
    if (!this.subscription) return false;
    // ActionCable.Subscription.perform returns the result of consumer.send,
    // which is `false` when the websocket isn't open. Typed as `boolean` here
    // for our usage; the @rails/actioncable types are missing.
    return this.subscription.perform("create_message", {
      contents: message,
      attachmentId: attachmentId,
      current_url: getCurrentSanitizedUrl()
    }) as boolean;
  }

  sendTypingNotification(typingState: TypingState): void {
    this.subscription?.perform("send_typing_notification", { typing_state: typingState });
  }
}
